import React from "react";
import type {
  RoutePoint,
  RouteTripGap,
  TripSplitReason,
} from "../../types/route";
import {
  formatDateTime,
  formatSpeed,
  formatBattery,
  formatDistance,
  formatDuration,
} from "../../utils/formatters";

const SPLIT_REASON_LABELS: Record<TripSplitReason, string> = {
  start: "Start",
  time_gap: "GPS gap",
  long_stop: "Long stop",
  teleport: "Transport",
};

interface RoutePointPopupProps {
  point: RoutePoint;
  tripIndex: number;
  pointIndex: number;
  totalTrips: number;
  onJumpToPoint?: () => void;
}

export const RoutePointPopup: React.FC<RoutePointPopupProps> = ({
  point,
  tripIndex,
  pointIndex,
  totalTrips,
  onJumpToPoint,
}) => {
  return (
    <div className="text-sm text-gray-700 min-w-[180px]">
      <div className="font-semibold text-gray-900 mb-2">
        Trip {tripIndex + 1} of {totalTrips} · Point {pointIndex + 1}
      </div>
      <div className="space-y-1">
        <div>
          <span className="text-gray-500">Time:</span>{" "}
          {formatDateTime(point.timestamp)}
        </div>
        <div>
          <span className="text-gray-500">Location:</span>{" "}
          {point.latitude.toFixed(6)}, {point.longitude.toFixed(6)}
        </div>
        <div>
          <span className="text-gray-500">Speed:</span>{" "}
          {formatSpeed(point.speed)}
        </div>
        {point.battery_level !== undefined && (
          <div>
            <span className="text-gray-500">Battery:</span>{" "}
            {formatBattery(point.battery_level)}
          </div>
        )}
        {point.heading !== undefined && (
          <div>
            <span className="text-gray-500">Heading:</span> {point.heading}°
          </div>
        )}
      </div>
      {onJumpToPoint && (
        <button
          type="button"
          onClick={onJumpToPoint}
          className="mt-3 text-xs text-blue-600 hover:text-blue-800 font-medium"
        >
          Jump to this point
        </button>
      )}
    </div>
  );
};

interface RouteGapPopupProps {
  gap: RouteTripGap;
}

export const RouteGapPopup: React.FC<RouteGapPopupProps> = ({ gap }) => {
  return (
    <div className="text-sm text-gray-700 min-w-[180px]">
      <div className="font-semibold text-gray-900 mb-2">
        Gap · {SPLIT_REASON_LABELS[gap.reason]}
      </div>
      <div className="space-y-1">
        <div>
          <span className="text-gray-500">Distance:</span>{" "}
          {formatDistance(gap.distance_km)}
        </div>
        <div>
          <span className="text-gray-500">Time gap:</span>{" "}
          {formatDuration(gap.time_gap_minutes)}
        </div>
        <div>
          <span className="text-gray-500">From:</span>{" "}
          {formatDateTime(gap.from_point.timestamp)}
        </div>
        <div>
          <span className="text-gray-500">To:</span>{" "}
          {formatDateTime(gap.to_point.timestamp)}
        </div>
      </div>
    </div>
  );
};
